import { useQuery } from '@apollo/client';
import { CircularProgress, Icon } from '@elipssolution/harfang';
import { mdiTrendingDown, mdiTrendingNeutral, mdiTrendingUp } from '@mdi/js';
import { Stack, Typography } from '@mui/material';
import { addMonths, addYears, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import numeral from 'numeral';
import { useCallback, useMemo, useState } from 'react';

import { FETCH_REVENUE, FetchRevenueType } from '../../../../../src/api/revenue';
import { ChartTypeEnum, PeriodicityEnum } from '../../../../../src/types/revenue';
import { WidgetExpandedComponentProps } from '../../../../../types/widget';
import BarChart from '../charts/BarChart';
import LineChart from '../charts/LineChart';
import { WidgetRevenueConfigurationType } from '../configuration/WidgetRevenueConfiguration';
import PeriodBanner from '../PeriodBanner';
import { calculatePercentageChange } from '../utils/calculatePercentageChange';
import { shortMonthsName } from '../utils/shortMonthsName';

const TODAY_DATE = new Date();

const ExpandedMobileWidgetRevenue = ({
	configuration,
}: WidgetExpandedComponentProps<WidgetRevenueConfigurationType>) => {
	const {
		accountRanges,
		isBalanceCumulative = false,
		periodicity = PeriodicityEnum.ANNUALLY,
		chartType = ChartTypeEnum.BAR_CHART,
		formula,
	} = configuration ?? {};

	const [date, setDate] = useState(TODAY_DATE);

	const handleDateChange = (shift: number) =>
		setDate(periodicity === PeriodicityEnum.ANNUALLY ? addYears(date, shift) : addMonths(date, shift));

	const { data: revenueData, loading: isRevenueLoading } = useQuery<FetchRevenueType>(FETCH_REVENUE, {
		skip: !periodicity || !date,
		variables: {
			revenueInput: {
				rangeAccount: accountRanges?.map(({ value }) => value),
				isBalanceCumulative,
				date,
				periodicity,
				formula,
			},
		},
	});

	const {
		revenue: { balance = '0', data = [{ name: '', previousValue: 0, value: 0 }], previousBalance = '0' },
	} = revenueData || { revenue: {} };

	const revenues = useMemo(
		() =>
			data?.map(({ name, previousValue, value }) => ({
				name: periodicity === PeriodicityEnum.ANNUALLY ? shortMonthsName[+name - 1] : name,
				previousValue: +previousValue,
				value: +value,
			})) || [],
		[data, periodicity],
	);

	const labelFormatter = useCallback(
		(label: string) =>
			`${label}${
				periodicity === PeriodicityEnum.MONTHLY
					? ` ${format(date, 'MMMM', {
							locale: fr,
					  })}`
					: ''
			}`,
		[date, periodicity],
	);
	const chartRenderer = useMemo(() => {
		switch (chartType) {
			case ChartTypeEnum.LINE_CHART:
				return <LineChart data={revenues} labelFormatter={labelFormatter} />;

			default:
				return <BarChart data={revenues} labelFormatter={labelFormatter} />;
		}
	}, [chartType, labelFormatter, revenues]);

	const percentageChange = calculatePercentageChange({ previousBalance, currentBalance: balance });

	const percentageIcon = useMemo(() => {
		if (percentageChange === 0) return mdiTrendingNeutral;

		return percentageChange > 0 ? mdiTrendingUp : mdiTrendingDown;
	}, [percentageChange]);

	return (
		<Stack flex={1}>
			{isRevenueLoading ? (
				<Stack flex={1} justifyContent="center" alignItems="center">
					<CircularProgress color="inherit" />
				</Stack>
			) : (
				<Stack gap={2} flex={1}>
					<PeriodBanner
						balance={balance}
						date={date}
						periodicity={periodicity}
						onDateChange={handleDateChange}
						previousBalance={previousBalance}
					/>

					{numeral(previousBalance).value() !== 0 && (
						<Stack flexDirection="row" justifyContent="center" alignItems="center" gap={2}>
							<Icon path={percentageIcon} size="large" />

							<Typography fontWeight="bold" variant="h6">
								{numeral(percentageChange).format('0,00.00')}%
							</Typography>
						</Stack>
					)}

					<Stack flex={1} paddingBottom={1} paddingTop={1}>
						{chartRenderer}
					</Stack>
				</Stack>
			)}
		</Stack>
	);
};

export default ExpandedMobileWidgetRevenue;
